import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { colors, radius } from "@forge/ui";

interface Conversation {
  user_id: string;
  user_name: string | null;
  user_avatar_url: string | null;
  last_message: string;
  last_message_at: number;
  unread_count: number;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  read: boolean;
  created_at: number;
}

interface ThreadResponse {
  messages: Message[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

function formatTimeShort(s: number): string {
  const d = new Date(s * 1000);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
  return d.toLocaleDateString();
}

function formatTimeOfDay(s: number): string {
  const d = new Date(s * 1000);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

function initial(name: string | null | undefined): string {
  return (name?.trim().charAt(0).toUpperCase() || "?");
}

const avatarBase: React.CSSProperties = {
  backgroundColor: colors.accent,
  color: colors.text,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  flexShrink: 0,
};

const panelBase: React.CSSProperties = {
  backgroundColor: colors.bg,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  overflow: "hidden",
};

export function MessagesPage() {
  const { user } = useAuth();
  const currentUserId = user?.id;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  const markedReadRef = useRef<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<Conversation[]>("/api/messages/conversations");
        if (!cancelled) setConversations(data || []);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load conversations");
      } finally {
        if (!cancelled) setLoadingConvs(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selectedUserId) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.get<Conversation[]>("/api/messages/conversations");
        setConversations(data || []);
      } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedUserId]);

  async function loadThread(userId: string, isPoll = false): Promise<void> {
    if (!isPoll) setLoadingThread(true);
    try {
      const data = await api.get<ThreadResponse>(`/api/messages/conversations/${userId}`);
      const newMessages = data.messages || [];
      setMessages(newMessages);
      if (currentUserId) {
        for (const msg of newMessages) {
          if (msg.sender_id !== currentUserId && !msg.read && !markedReadRef.current.has(msg.id)) {
            markedReadRef.current.add(msg.id);
            try { await api.put(`/api/messages/${msg.id}/read`, {}); } catch { /* ignore */ }
          }
        }
        setConversations((prev) => prev.map((c) => c.user_id === userId ? { ...c, unread_count: 0 } : c));
      }
    } catch (e: unknown) {
      if (!isPoll) setError(e instanceof Error ? e.message : "Failed to load messages");
    } finally {
      if (!isPoll) setLoadingThread(false);
    }
  }

  useEffect(() => {
    if (!selectedUserId) {
      // synchronous reset: when the user backs out of a thread we must
      // clear the prior conversation immediately so the UI doesn't show
      // stale messages. There's no way to derive "cleared" from props.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessages([]);
      markedReadRef.current.clear();
      return;
    }
    markedReadRef.current.clear();
    loadThread(selectedUserId);
    const interval = setInterval(() => { loadThread(selectedUserId, true); }, 15000);
    return () => clearInterval(interval);
    // loadThread intentionally omitted: it reads currentUserId and markedReadRef
    // via closure; re-running the poll when those change would tear down and
    // re-create the interval unnecessarily. The 15s poll is the source of
    // truth for refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, currentUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  function selectConversation(conv: Conversation): void {
    setSelectedUserId(conv.user_id);
    setSelectedUserName(conv.user_name || "User");
    setMessages([]);
  }

  function goBackToList(): void {
    setSelectedUserId(null); setSelectedUserName(""); setMessages([]);
  }

  async function sendMessage(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!selectedUserId || !input.trim() || sending) return;
    setSending(true);
    const content = input.trim();
    setInput("");
    try {
      await api.post<Message>("/api/messages", { receiver_id: selectedUserId, content });
      await loadThread(selectedUserId, true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  const showList = !isMobile || !selectedUserId;
  const showThread = !isMobile || !!selectedUserId;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Messages</h1>
        {error && <div style={styles.errorBanner}>{error}</div>}
      </div>
      <div style={styles.grid}>
        <div style={{ ...styles.conversationsCol, display: showList ? "flex" : "none" }}>
          {loadingConvs ? (
            <div style={styles.muted}>Loading conversations...</div>
          ) : conversations.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyTitle}>No conversations yet</div>
              <div style={styles.emptySubtitle}>Messages with your trainer will appear here.</div>
            </div>
          ) : (
            <ul style={styles.convList}>
              {conversations.map((c) => (
                <li
                  key={c.user_id}
                  style={{ ...styles.convItem, ...(selectedUserId === c.user_id ? styles.convItemActive : {}) }}
                  onClick={() => selectConversation(c)}
                >
                  <div style={styles.avatar}>{initial(c.user_name)}</div>
                  <div style={styles.convBody}>
                    <div style={styles.convTopRow}>
                      <span style={styles.convName}>{c.user_name || "User"}</span>
                      <span style={styles.convTime}>{formatTimeShort(c.last_message_at)}</span>
                    </div>
                    <div style={styles.convBottomRow}>
                      <span style={styles.convPreview}>{c.last_message}</span>
                      {c.unread_count > 0 && (
                        <span style={styles.unreadPill}>{c.unread_count > 9 ? "9+" : c.unread_count}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ ...styles.threadCol, display: showThread ? "flex" : "none" }}>
          {!selectedUserId ? (
            <div style={styles.empty}>
              <div style={styles.emptyTitle}>Select a conversation</div>
              <div style={styles.emptySubtitle}>Pick someone on the left to view your messages.</div>
            </div>
          ) : (
            <>
              <div style={styles.threadHeader}>
                {isMobile && (
                  <button type="button" onClick={goBackToList} style={styles.backBtn} aria-label="Back">‹</button>
                )}
                <div style={styles.avatarSmall}>{initial(selectedUserName)}</div>
                <div style={styles.threadHeaderName}>{selectedUserName}</div>
              </div>
              <div style={styles.messagesScroll}>
                {loadingThread && messages.length === 0 ? (
                  <div style={styles.muted}>Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div style={styles.muted}>No messages yet. Send one to start the conversation.</div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === currentUserId;
                    return (
                      <div key={m.id} style={{ ...styles.bubbleRow, justifyContent: mine ? "flex-end" : "flex-start" }}>
                        <div style={{ ...styles.bubble, ...(mine ? styles.bubbleMine : styles.bubbleTheirs) }}>
                          <div style={styles.bubbleText}>{m.content}</div>
                          <div style={{ ...styles.bubbleTime, color: mine ? "rgba(255,255,255,0.7)" : colors.textDim }}>
                            {formatTimeOfDay(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={sendMessage} style={styles.composer}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  style={styles.composerInput}
                  disabled={sending}
                  aria-label="Message input"
                />
                <button
                  type="submit"
                  style={{ ...styles.sendBtn, opacity: input.trim() && !sending ? 1 : 0.5 }}
                  disabled={!input.trim() || sending}
                >
                  {sending ? "..." : "Send"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { fontFamily: "Inter, system-ui, Arial", color: colors.text, minHeight: "calc(100vh - 56px - 60px)", display: "flex", flexDirection: "column" },
  header: { marginBottom: 12 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  errorBanner: { marginTop: 8, padding: "8px 12px", backgroundColor: "var(--color-error-bg)", color: colors.danger, borderRadius: 6, fontSize: 13 },
  grid: { display: "grid", gridTemplateColumns: "minmax(260px, 1fr) 2fr", gap: 16, flex: 1, minHeight: 0 },
  conversationsCol: { ...panelBase, flexDirection: "column" },
  threadCol: { ...panelBase, flexDirection: "column", minHeight: 0 },
  convList: { listStyle: "none", margin: 0, padding: 0, overflowY: "auto", flex: 1 },
  convItem: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" },
  convItemActive: { backgroundColor: colors.surfaceElevated },
  avatar: { ...avatarBase, width: 40, height: 40, borderRadius: "50%", fontSize: 16 },
  avatarSmall: { ...avatarBase, width: 32, height: 32, borderRadius: "50%", fontSize: 14 },
  convBody: { flex: 1, minWidth: 0 },
  convTopRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  convName: { fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  convTime: { fontSize: 11, color: colors.textDim, flexShrink: 0 },
  convBottomRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 2 },
  convPreview: { fontSize: 13, color: colors.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 },
  unreadPill: { backgroundColor: colors.accent, color: colors.text, borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700, flexShrink: 0 },
  threadHeader: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${colors.border}` },
  backBtn: { background: "transparent", border: "none", color: colors.accent, fontSize: 24, cursor: "pointer", padding: 0, lineHeight: 1 },
  threadHeaderName: { fontWeight: 600, fontSize: 15 },
  messagesScroll: { flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 8, minHeight: 0 },
  bubbleRow: { display: "flex", width: "100%" },
  bubble: { maxWidth: "75%", padding: "8px 12px", borderRadius: 12, wordBreak: "break-word" },
  bubbleMine: { backgroundColor: colors.accent, color: colors.text, borderBottomRightRadius: 2 },
  bubbleTheirs: { backgroundColor: colors.surfaceElevated, color: colors.text, borderBottomLeftRadius: 2 },
  bubbleText: { fontSize: 14, whiteSpace: "pre-wrap" },
  bubbleTime: { fontSize: 10, marginTop: 4, textAlign: "right" },
  composer: { display: "flex", gap: 8, padding: 10, borderTop: `1px solid ${colors.border}`, backgroundColor: colors.bg },
  composerInput: { flex: 1, padding: "10px 12px", borderRadius: radius.md, border: `1px solid ${colors.border}`, backgroundColor: colors.bg, color: colors.text, fontSize: 14, outline: "none" },
  sendBtn: { padding: "10px 18px", borderRadius: radius.md, border: "none", backgroundColor: colors.accent, color: colors.text, fontWeight: 600, cursor: "pointer", fontSize: 14 },
  empty: { padding: 24, textAlign: "center" },
  emptyTitle: { fontSize: 16, fontWeight: 600, marginBottom: 4 },
  emptySubtitle: { fontSize: 13, color: colors.textDim },
  muted: { padding: 24, textAlign: "center", color: colors.textDim, fontSize: 14 },
};
